import { ButtonWithTarget } from 'layouts/button-with-target'
import SectionType from 'models/types/ts/sectionType'
import React from 'react'
import { GlobalState } from 'types'
import { useSelector, useDispatch } from 'react-redux'
import { isLoggedIn } from 'packages/authentication'
import { login } from 'packages/authentication/lib/actions'
import getConfig from 'config/web'
import { Modal, useModal } from 'layouts/modal'
import ReactMarkdown from 'utils/markdown'
import cx from 'classnames'
import { useInView } from 'react-intersection-observer'
import { CountDown } from 'features/count-down'
import { getQuery } from 'lib/location/selectors'
import RichText from 'pages/news/components/newsDetail/components/rich-text'
import { PlexForGoodTranslations } from 'pages/plex-for-good/PlexForGood'
import { RichTextMicroCopy } from 'pages/news/components/newsDetail/components/rich-text/RichText'
import DonationForm from '../donation-form'
import style from './CurrentEvents.module.scss'

const { authentication } = getConfig()

interface Props {
    event: SectionType
    everGreen: SectionType
    translations: PlexForGoodTranslations
}

interface CurrentEventProps {
    event: SectionType
    compact?: boolean
    translations: PlexForGoodTranslations
}

const CurrentEvent = ({
    event,
    compact = false,
    translations,
}: CurrentEventProps): JSX.Element => {
    const loggedIn = useSelector((state: GlobalState) => isLoggedIn(state))
    const dispatch = useDispatch()
    const query = useSelector((state: GlobalState) => getQuery(state))
    const [eventActive, setEventActive] = React.useState(
        !event.date || new Date(event.date) >= new Date()
    )
    const [isOpen, setIsOpen] = React.useState(
        query && query.campaign === event.identifier
    )

    function toggleModal(): void {
        const newState = !isOpen
        setIsOpen(newState)
        const url = new URL(window.location.href)
        if (newState) {
            url.searchParams.set('campaign', event.identifier)
        } else {
            url.searchParams.delete('campaign')
        }

        window.history.pushState({}, '', url)
    }
    const { ref, inView } = useInView({
        threshold: 0.15,
        triggerOnce: true,
    })

    const campaigns = event?.contentCollection?.items || []

    const content = campaigns[0]

    if (!content) {
        return <></>
    }

    const name = event.headline || content.name
    const body = event.body || content.body
    const { url, description } = event.imageFile || content.imageFile

    const CTAButton = !loggedIn ? (
        <div
            className={style.ctaContainer}
            onClick={() => {
                dispatch(
                    login(
                        `plex-for-good?campaign=${event.identifier}`,
                        authentication
                    )
                )
            }}
        >
            <ButtonWithTarget backgroundColor="#F67C0F">
                <RichTextMicroCopy document={translations.loginToDonate} />
            </ButtonWithTarget>
        </div>
    ) : (
        <div
            className={style.ctaContainer}
            onClick={() => {
                toggleModal()
            }}
        >
            <ButtonWithTarget backgroundColor="#F67C0F">
                {event.buttonText}
            </ButtonWithTarget>
        </div>
    )

    return (
        <div
            ref={ref}
            className={cx(style.eventContainer, {
                [style.compact]: compact,
                [style.visible]: inView,
            })}
        >
            <div className={style.eventContent}>
                <div className={style.headline}>{name}</div>
                <ReactMarkdown
                    className={style.descriptionMarkdown}
                    source={body}
                />
                <div className={style.campaignIcons}>
                    {campaigns.length > 1
                        ? campaigns.map((campaign) => {
                              return (
                                  <img
                                      key={(campaign.data as { id: string }).id}
                                      alt={campaign.buttonText}
                                      src={campaign.image}
                                  />
                              )
                          })
                        : null}
                </div>
                {eventActive ? (
                    CTAButton
                ) : (
                    <div className={style.ctaContainer}>
                        <ButtonWithTarget
                            buttonClassNames={{ root: style.campaignEnded }}
                            targetClassNames={{ root: style.campaignEnded }}
                            disabled
                            color="#B0B0B0"
                            backgroundColor="#474746"
                        >
                            <RichTextMicroCopy
                                document={translations.campaignHasEnded}
                            />
                        </ButtonWithTarget>
                    </div>
                )}
            </div>
            <div className={style.imageContainer}>
                <img alt={description} src={url} />
                {event.date && (
                    <CountDown
                        classNames={{
                            completed: style.completed,
                            title: style.title,
                            countdownContainer: style.countdownContainer,
                            timeUnit: style.timeUnit,
                            number: style.number,
                            label: style.label,
                            wrapper: style.countdownWrapper,
                        }}
                        title={event.teaser}
                        date={String(event.date)}
                        withTarget={false}
                        onComplete={() => setEventActive(false)}
                    />
                )}
            </div>
            <Modal
                modalClassName={style.modal}
                isOpen={isOpen}
                hide={()=> toggleModal()}
                closeIconClassName={style.modalCloseIcon}
                displayCloseIconCross
            >
                <DonationForm
                    campaigns={campaigns.map((c) => ({
                        id: (c.data as { id: string }).id,
                        name: c.buttonText,
                        icon: c.image,
                        description: c.body,
                        title: c.headline,
                        imageFile: c.imageFile,
                    }))}
                    translations={translations}
                />
            </Modal>
        </div>
    )
}

const CurrentEvents = ({
    event,
    everGreen,
    translations,
}: Props): JSX.Element => {
    return (
        <div className={style.currentEventsWrapper}>
            <div className={style.currentEventsContainer}>
                {event && (
                    <CurrentEvent event={event} translations={translations} />
                )}
                {everGreen && (
                    <CurrentEvent
                        event={everGreen}
                        compact={!!event}
                        translations={translations}
                    />
                )}
            </div>
        </div>
    )
}

export default CurrentEvents
